import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Phone, Mail, ShieldCheck, CheckCircle2, X } from "lucide-react";

export default function ServiceDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [galleryImages, setGalleryImages] = useState([]);
  const [activeImage, setActiveImage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setService(null);
    setGalleryImages([]);

    // Hent alle ydelser for at finde den rigtige baseret på slug_name
    fetch("https://api.dev.auxo.dk/public/content/services")
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          const foundService = data.find((s) => s.slug_name === slug);
          if (foundService) {
            setService(foundService);

            // Opdater sidetitel og meta-beskrivelse dynamisk
            if (foundService.meta_title?.da) {
              document.title = foundService.meta_title.da;
            } else {
              document.title = `${foundService.title.da} - BMT TAG ApS`;
            }

            if (foundService.meta_description?.da) {
              const metaDesc = document.querySelector('meta[name="description"]');
              if (metaDesc) {
                metaDesc.setAttribute("content", foundService.meta_description.da);
              }
            }

            // Hent tilknyttet galleri hvis det findes
            if (foundService.gallery_slug) {
              fetch("https://api.dev.auxo.dk/public/content/gallery")
                .then((res) => res.json())
                .then((galleries) => {
                  if (Array.isArray(galleries)) {
                    const foundGallery = galleries.find((g) => g.slug_name === foundService.gallery_slug);
                    if (foundGallery && foundGallery.images) {
                      setGalleryImages(foundGallery.images);
                    }
                  }
                })
                .catch((err) => console.error("Fejl ved hentning af tilknyttet galleri:", err));
            }
          } else {
            // Hvis ydelsen ikke findes, omdiriger til 404
            navigate("/404", { replace: true });
          }
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error("Fejl ved hentning af ydelse:", err);
        setLoading(false);
      });
  }, [slug, navigate]);

  if (loading) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-24 text-center">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-brand-accent border-t-transparent" />
        <p className="mt-4 text-slate-500">Indlæser arbejdsopgave...</p>
      </div>
    );
  }

  if (!service) return null;

  return (
    <div className="pb-20">
      {/* Hero / Header Sektion */}
      <section className="relative bg-brand-blue-dark text-white py-16 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={
              service.image && service.image[0]
                ? `https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/${service.image[0].replace("https://assets.auxodev.dk/", "")}`
                : "https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/306cd3bb-e6f1-472d-9f9e-c228e4c6c6b6/modern-danish-roof-roofing.jpg"
            }
            alt={service.title.da}
            className="h-full w-full object-cover opacity-25"
          />
          <div className="absolute inset-0 bg-brand-blue-dark/80" />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center space-y-4">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-white/80 hover:text-white transition-colors mb-2 cursor-pointer"
          >
            <ArrowLeft className="h-4 w-4" />
            Tilbage til forsiden
          </Link>
          <h1 className="text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl font-display">
            {service.title.da}
          </h1>
          <div className="flex justify-center items-center gap-2 text-sm text-slate-300">
            <ShieldCheck className="h-5 w-5 text-white" />
            <span>Professionelt tagarbejde med garanti</span>
          </div>
        </div>
      </section>

      {/* Indhold Sektion */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Brødtekst (venstre) */}
          <div className="lg:col-span-8 space-y-8">
            <div
              className="prose-custom text-slate-600 text-base"
              dangerouslySetInnerHTML={{ __html: service.content.da }}
            />

            {/* Galleri tilknyttet ydelsen */}
            {galleryImages.length > 0 && (
              <div className="space-y-6 pt-8 border-t border-brand-neutral-border">
                <h3 className="text-2xl font-bold text-brand-blue-dark">
                  Billeder af {service.title.da}
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {galleryImages.map((imgUrl, index) => (
                    <button
                      key={index}
                      onClick={() => setActiveImage(imgUrl)}
                      className="aspect-video overflow-hidden rounded-none border border-brand-neutral-border bg-slate-100 hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-brand-accent cursor-pointer"
                    >
                      <img
                        src={`https://assets.auxodev.dk/cdn-cgi/image/width=300,height=200,fit=cover,quality=80,format=auto/${imgUrl.replace("https://assets.auxodev.dk/", "")}`}
                        alt={`${service.title.da} reference ${index + 1}`}
                        className="w-full h-full object-cover rounded-none"
                      />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar (højre) */}
          <div className="lg:col-span-4 space-y-8">
            {/* CTA boks */}
            <div className="bg-white rounded-xl border border-brand-neutral-border p-6 shadow-sm space-y-6">
              <h4 className="text-lg font-bold text-brand-blue-dark border-b border-brand-neutral-border pb-3">
                Få et uforpligtende tilbud
              </h4>
              <p className="text-sm text-slate-600 leading-relaxed">
                Har du brug for rådgivning eller et uforpligtende tilbud på <strong>{service.title.da}</strong>? Vi står klar til at hjælpe dig.
              </p>
              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-2.5">
                  <Phone className="h-5 w-5 text-brand-accent shrink-0" />
                  <a href="tel:+4523287183" className="font-semibold text-brand-blue-dark hover:text-brand-accent transition-colors whitespace-nowrap">
                    23 28 71 83
                  </a>
                </div>
                <div className="flex items-center gap-2.5">
                  <Mail className="h-5 w-5 text-brand-accent shrink-0" />
                  <a href="mailto:mb@bmttag.dk" className="font-semibold text-brand-blue-dark hover:text-brand-accent transition-colors whitespace-nowrap">
                    mb@bmttag.dk
                  </a>
                </div>
              </div>
              <Link
                to="/indhent-tilbud"
                className="block w-full text-center rounded-md bg-brand-accent py-3 text-sm font-bold text-white shadow-sm hover:bg-brand-accent-hover transition-colors cursor-pointer"
              >
                Indhent tilbud nu
              </Link>
            </div>

            {/* Certificeringer */}
            <div className="bg-brand-neutral-light rounded-xl border border-brand-neutral-border p-6 space-y-4">
              <h4 className="text-sm font-bold text-brand-blue-dark uppercase tracking-wider">
                Hvorfor vælge os?
              </h4>
              <ul className="space-y-3 text-sm text-slate-600">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-5 w-5 text-brand-accent shrink-0 mt-0.5" />
                  <span>Over 35 års erfaring i branchen</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-5 w-5 text-brand-accent shrink-0 mt-0.5" />
                  <span>Tredobbelt forsikringsdækket garanti</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-5 w-5 text-brand-accent shrink-0 mt-0.5" />
                  <span>Medlem af Dansk Byggeri & INTA</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox / Stor visning af galleribilleder */}
      {activeImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4">
          <button
            onClick={() => setActiveImage(null)}
            className="absolute top-4 right-4 text-white hover:text-brand-accent p-2 cursor-pointer"
          >
            <X className="h-8 w-8" />
          </button>
          <div className="max-w-4xl max-h-[85vh] overflow-hidden rounded-none">
            <img
              src={`https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=90,format=auto/${activeImage.replace("https://assets.auxodev.dk/", "")}`}
              alt="Reference stor visning"
              className="w-full h-auto max-h-[85vh] object-contain rounded-none"
            />
          </div>
        </div>
      )}
    </div>
  );
}
